import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { PersonalShell } from './components/PersonalShell';
import { EnterpriseShell } from './components/EnterpriseShell';
import { LandingPage } from './pages/shared/LandingPage';
import { AuthPage } from './pages/shared/AuthPage';
import { PersonalDashboard } from './pages/personal/PersonalDashboard';
import { PersonalChart } from './pages/personal/PersonalChart';
import { Synergy } from './pages/personal/Synergy';
import { ProfilePage } from './pages/personal/ProfilePage';
import { OnboardingPage } from './pages/personal/OnboardingPage';
import { EnterpriseDashboard } from './pages/enterprise/EnterpriseDashboard';
import { Candidates } from './pages/enterprise/Candidates';
import { TeamDynamics } from './pages/enterprise/TeamDynamics';
import { ApiKeys } from './pages/enterprise/ApiKeys';
import { AdminPayments } from './pages/enterprise/AdminPayments';
import { AdminNotifications } from './pages/enterprise/AdminNotifications';
import { AdminFreeForm } from './pages/personal/AdminFreeForm';
import { AdminQRIS } from './pages/enterprise/AdminQRIS';
import { API_URL } from './config';
import { Spin } from 'antd';

interface PersonalLayoutProps {
  token: string | null;
  memberStatus: 'premium' | 'admin';
  onLogout: () => void;
  email: string | null;
}

const PersonalProtectedLayout: React.FC<PersonalLayoutProps> = ({ token, memberStatus, onLogout, email }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<'premium' | 'non_premium' | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!token) return;
    const checkSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/personal/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setSubscriptionStatus(result.is_premium ? 'premium' : 'non_premium');
        } else if (res.status === 401) {
          onLogout();
        } else {
          setSubscriptionStatus('non_premium');
        }
      } catch (e) {
        setSubscriptionStatus('non_premium');
      }
    };
    checkSubscription();
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (subscriptionStatus === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8f9fa', flexDirection: 'column', gap: '16px' }}>
         <Spin size="large" />
         <span style={{ fontWeight: 800, color: '#1a202c' }}>Memuat Status Membership...</span>
      </div>
    );
  }

  // Non-premium users (not admin) — redirect to onboarding/payment page
  // Admin routes and onboarding page itself are exempt
  if (subscriptionStatus === 'non_premium' && 
      memberStatus !== 'admin' &&
      location.pathname !== '/personal/onboarding' &&
      !location.pathname.startsWith('/personal/admin')) {
    return <Navigate to="/personal/onboarding" replace />;
  }

  return (
    <PersonalShell 
      memberStatus={memberStatus}
      onLogout={onLogout} 
      userEmail={email || 'user@bazi.com'}
    >
      <Outlet />
    </PersonalShell>
  );
};

const EnterpriseLayout: React.FC = () => {
  return (
    <EnterpriseShell 
      tenantName="Odoo HR Enterprise" 
      onLogout={() => window.location.href = '/'} 
      userEmail="hr.admin@odoo.com"
    >
      <Outlet />
    </EnterpriseShell>
  );
};

// Admin emails with free full access
const ADMIN_EMAILS = ['admin@bazi.com', 'developer@bazi.com', 'support@bazi.com'];

function getMemberStatus(userEmail: string | null): 'premium' | 'admin' {
  if (!userEmail) return 'premium';
  return ADMIN_EMAILS.includes(userEmail.toLowerCase().trim()) ? 'admin' : 'premium';
}

export const App: React.FC = () => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('personal_token'));
  const [email, setEmail] = useState<string | null>(localStorage.getItem('personal_email'));

  const memberStatus = getMemberStatus(email);

  const handleAuthSuccess = (newToken: string, newEmail: string, isNewRegistration?: boolean) => {
    localStorage.setItem('personal_token', newToken);
    localStorage.setItem('personal_email', newEmail);
    if (isNewRegistration) {
      localStorage.setItem('show_onboarding', 'true');
    }
    setToken(newToken);
    setEmail(newEmail);
  };

  const handleLogout = () => {
    localStorage.removeItem('personal_token');
    localStorage.removeItem('personal_email');
    localStorage.removeItem('show_onboarding');
    setToken(null);
    setEmail(null);
  };

  return (
    <Router>
      <Routes>
        {/* Shared / Landing */}
        <Route path="/" element={<LandingPage />} />
        <Route 
          path="/login" 
          element={
            token ? <Navigate to="/personal/chart" replace /> : <AuthPage onAuthSuccess={handleAuthSuccess} API_URL={API_URL} />
          } 
        />

        {/* PERSONAL Portal */}
        <Route element={<PersonalProtectedLayout token={token} memberStatus={memberStatus} onLogout={handleLogout} email={email} />}>
          <Route path="/personal/dashboard" element={<PersonalDashboard memberStatus={memberStatus} />} />
          <Route path="/personal/chart" element={<PersonalChart token={token || ''} API_URL={API_URL} />} />
          <Route path="/personal/synergy" element={<Synergy token={token || ''} API_URL={API_URL} isAdmin={memberStatus === 'admin'} />} />
          <Route path="/personal/onboarding" element={<OnboardingPage token={token || ''} API_URL={API_URL} />} />
          <Route path="/personal/profile" element={<ProfilePage token={token || ''} API_URL={API_URL} />} />
          <Route path="/personal/admin/payments" element={<AdminPayments token={token || ''} API_URL={API_URL} userEmail={email || ''} />} />
          <Route path="/personal/admin/notifications" element={<AdminNotifications token={token || ''} API_URL={API_URL} />} />
          <Route path="/personal/admin/free-form" element={<AdminFreeForm token={token || ''} API_URL={API_URL} />} />
          <Route path="/personal/admin/qris" element={<AdminQRIS token={token || ''} API_URL={API_URL} />} />
        </Route>

        {/* ENTERPRISE Portal */}
        <Route element={<EnterpriseLayout />}>
          <Route path="/enterprise/dashboard" element={<EnterpriseDashboard tenantName="Odoo HR Enterprise" />} />
          <Route path="/enterprise/candidates" element={<Candidates />} />
          <Route path="/enterprise/dynamics" element={<TeamDynamics />} />
          <Route path="/enterprise/api-keys" element={<ApiKeys />} />
          <Route path="/enterprise/settings" element={<div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">Settings Page Placeholder</div>} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};
export default App;
